/**
 * Claims Manager — async thunks for the three read endpoints.
 *
 *   GET /accounts/{currentAccountUuid}/claims-manager/summary          (L1)
 *   GET /accounts/{currentAccountUuid}/claims-manager/account-detail   (L2)
 *   GET /accounts/{currentAccountUuid}/claims-manager/cpt-claims       (L3)
 *
 * Export (`/claims-manager/export`) is a file download rather than
 * a JSON envelope, so it is handled in a page-side helper (see
 * `pages/claims-manager/lib/exportSummary.js`) instead of a thunk.
 *
 * All three endpoints share the QHS envelope
 * `{ data: <body>, correlationUUID }`.
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '@shared/api/client';

const buildQueryString = (params) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    search.append(k, Array.isArray(v) ? v.join(',') : v);
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
};

const unwrap = (response) => response.data?.data ?? response.data;

/**
 * Portfolio summary (L1). `from`/`to` are required ISO YYYY-MM-DD
 * strings; the account and CPT lists are optional (omitted = all
 * in scope). Sorting/search happen client-side against `rows`.
 */
export const fetchClaimsManagerSummary = createAsyncThunk(
  'claimsManager/fetchSummary',
  async (
    { accountUuid, from, to, cptCodes = [], accountUuids = [] },
    { rejectWithValue }
  ) => {
    try {
      const qs = buildQueryString({
        from,
        to,
        cptCodes: cptCodes.length ? cptCodes : undefined,
        accountUuids: accountUuids.length ? accountUuids : undefined,
      });
      const response = await client.get(
        `/accounts/${accountUuid}/claims-manager/summary${qs}`
      );
      if (response.ok) {
        const body = unwrap(response);
        return {
          ok: true,
          data: {
            rows: body.rows || [],
            summary: body.summary || null,
            accountsInScope: body.accountsInScope ?? null,
            generatedAt: body.generatedAt || null,
          },
        };
      }
      return rejectWithValue({
        error: { message: response.data?.message || 'Failed to load Claims Manager summary' },
      });
    } catch (error) {
      return rejectWithValue({
        error: { message: error.message || 'Failed to load Claims Manager summary' },
      });
    }
  }
);

/**
 * Account detail (L2). Returns the CPT mix for one child account
 * plus a `summaryRow` whose numbers match the L1 row exactly.
 */
export const fetchClaimsManagerAccountDetail = createAsyncThunk(
  'claimsManager/fetchAccountDetail',
  async ({ accountUuid, targetAccountUuid, from, to, cptCodes = [] }, { rejectWithValue }) => {
    try {
      const qs = buildQueryString({
        targetAccountUuid,
        from,
        to,
        cptCodes: cptCodes.length ? cptCodes : undefined,
      });
      const response = await client.get(
        `/accounts/${accountUuid}/claims-manager/account-detail${qs}`
      );
      if (response.ok) {
        const body = unwrap(response);
        return {
          ok: true,
          data: {
            account: body.account || null,
            summaryRow: body.summaryRow || null,
            perCpt: body.perCpt || [],
            generatedAt: body.generatedAt || null,
          },
        };
      }
      return rejectWithValue({
        error: { message: response.data?.message || 'Failed to load account detail' },
      });
    } catch (error) {
      return rejectWithValue({
        error: { message: error.message || 'Failed to load account detail' },
      });
    }
  }
);

/**
 * CPT claims (L3). Every claim carrying `cptCode` at the target
 * account inside `[from, to]`.
 */
export const fetchClaimsManagerCptClaims = createAsyncThunk(
  'claimsManager/fetchCptClaims',
  async ({ accountUuid, targetAccountUuid, cptCode, from, to }, { rejectWithValue }) => {
    try {
      const qs = buildQueryString({ targetAccountUuid, cptCode, from, to });
      const response = await client.get(
        `/accounts/${accountUuid}/claims-manager/cpt-claims${qs}`
      );
      if (response.ok) {
        const body = unwrap(response);
        return {
          ok: true,
          data: {
            cptCode: body.cptCode,
            ispPrice: body.ispPrice ?? null,
            claims: body.claims || [],
            generatedAt: body.generatedAt || null,
          },
        };
      }
      return rejectWithValue({
        error: { message: response.data?.message || 'Failed to load CPT claims' },
      });
    } catch (error) {
      return rejectWithValue({
        error: { message: error.message || 'Failed to load CPT claims' },
      });
    }
  }
);

/**
 * Absolute URL for the export endpoint. The page-level export
 * helper builds an `<a href>` from it (server responds with a
 * `Content-Disposition` file download; no JSON envelope).
 */
export const buildClaimsManagerExportUrl = ({
  accountUuid,
  format = 'xlsx',
  from,
  to,
  cptCodes = [],
  accountUuids = [],
}) => {
  const qs = buildQueryString({
    format,
    from,
    to,
    cptCodes: cptCodes.length ? cptCodes : undefined,
    accountUuids: accountUuids.length ? accountUuids : undefined,
  });
  return `/accounts/${accountUuid}/claims-manager/export${qs}`;
};
